import { useCallback, useEffect, useRef, useState } from "react";
import { api, fmtBRL, API, extractError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Calculator, Users, DollarSign, Clock, Printer, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AuthContext";

export default function AdminDP() {
  const { admin } = useAdminAuth();
  const isMaster = admin?.role === "ADMIN_MASTER";
  const [funcs, setFuncs] = useState([]);
  const [folhas, setFolhas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [competencia, setCompetencia] = useState(new Date().toISOString().slice(0, 7));
  const [modalOpen, setModalOpen] = useState(false);
  const [folhaOpen, setFolhaOpen] = useState(false);
  const [selFunc, setSelFunc] = useState(null);
  const [form, setForm] = useState({ nome: "", cpf: "", cargo: "", salario_base: "", data_admissao: "", telefone: "", email: "", endereco: "", carga_horaria_mensal: "220", dia_pagamento: "5" });
  const [folhaForm, setFolhaForm] = useState({ horas_extras: "0", percentual_hora_extra: "50", descontos: "0", beneficios: "0" });
  // Edição de folha existente
  const [folhaEditOpen, setFolhaEditOpen] = useState(false);
  const [folhaEditId, setFolhaEditId] = useState(null);
  const [folhaEditForm, setFolhaEditForm] = useState({ horas_extras: "0", percentual_hora_extra: "50", descontos: "0", beneficios: "0", observacoes: "" });
  // Edição de funcionário
  const [editOpen, setEditOpen] = useState(false);
  const [editFunc, setEditFunc] = useState(null);
  const [editForm, setEditForm] = useState({ nome: "", cargo: "", salario_base: "", telefone: "", email: "", endereco: "", carga_horaria_mensal: "220", dia_pagamento: "5" });

  const load = useCallback(async () => {
    try {
      const [f, fo, r] = await Promise.all([
        api.get("/dp/funcionarios"),
        api.get(`/dp/folha?competencia=${competencia}`),
        api.get(`/dp/folha/resumo/${competencia}`),
      ]);
      setFuncs(f.data);
      setFolhas(fo.data);
      setResumo(r.data);
    } catch (e) {
      toast.error(extractError(e) || "Falha ao carregar");
    }
  }, [competencia]);

  useEffect(() => { load(); }, [load]);

  const criar = async () => {
    try {
      const payload = {
        ...form,
        salario_base: parseFloat(form.salario_base),
        carga_horaria_mensal: parseFloat(form.carga_horaria_mensal),
        dia_pagamento: parseInt(form.dia_pagamento, 10) || 5,
        data_admissao: new Date(form.data_admissao).toISOString(),
      };
      await api.post("/dp/funcionarios", payload);
      toast.success("Funcionário cadastrado");
      setModalOpen(false);
      setForm({ nome: "", cpf: "", cargo: "", salario_base: "", data_admissao: "", telefone: "", email: "", endereco: "", carga_horaria_mensal: "220", dia_pagamento: "5" });
      load();
    } catch (e) {
      toast.error(extractError(e) || "Erro ao cadastrar");
    }
  };

  const abrirEdicaoFolha = (f) => {
    setFolhaEditId(f.id);
    setFolhaEditForm({
      horas_extras: String(f.horas_extras ?? 0),
      percentual_hora_extra: String(f.percentual_hora_extra ?? 50),
      descontos: String(f.descontos ?? 0),
      beneficios: String(f.beneficios ?? 0),
      observacoes: f.observacoes || "",
    });
    setFolhaEditOpen(true);
  };

  const salvarEdicaoFolha = async () => {
    const safeNum = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    try {
      await api.patch(`/dp/folha/${folhaEditId}`, {
        horas_extras: safeNum(folhaEditForm.horas_extras),
        percentual_hora_extra: safeNum(folhaEditForm.percentual_hora_extra),
        descontos: safeNum(folhaEditForm.descontos),
        beneficios: safeNum(folhaEditForm.beneficios),
        observacoes: folhaEditForm.observacoes,
      });
      toast.success("Folha atualizada");
      setFolhaEditOpen(false);
      setFolhaEditId(null);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao atualizar folha");
    }
  };

  const excluirFolha = async (id, nome) => {
    if (!window.confirm(`Excluir a folha de ${nome}?`)) return;
    try {
      await api.delete(`/dp/folha/${id}`);
      toast.success("Folha excluída");
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao excluir");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Desligar este funcionário? Ele fica no histórico como inativo — use 'Excluir permanentemente' para removê-lo do banco.")) return;
    await api.delete(`/dp/funcionarios/${id}`);
    toast.success("Funcionário desligado");
    load();
  };

  const delPermanente = async (f) => {
    const alvo = f.nome || "este funcionário";
    if (!window.confirm(
      `⚠️ EXCLUSÃO PERMANENTE de ${alvo}\n\n` +
      `Esta ação remove definitivamente:\n` +
      `- Cadastro do funcionário\n` +
      `- Todas as escalas vinculadas\n` +
      `- Todas as folhas de pagamento históricas\n\n` +
      `Esta ação NÃO pode ser desfeita. Confirma?`
    )) return;
    try {
      const { data } = await api.delete(`/dp/funcionarios/${f.id}/permanent`);
      toast.success(
        `${data.nome} removido permanentemente · ${data.escalas_removidas} escala(s) e ${data.folhas_removidas} folha(s) apagadas`
      );
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao excluir permanentemente");
    }
  };

  const openEdit = (f) => {
    setEditFunc(f);
    setEditForm({
      nome: f.nome || "",
      cargo: f.cargo || "",
      salario_base: String(f.salario_base ?? ""),
      telefone: f.telefone || "",
      email: f.email || "",
      endereco: f.endereco || "",
      carga_horaria_mensal: String(f.carga_horaria_mensal ?? "220"),
      dia_pagamento: String(f.dia_pagamento ?? "5"),
    });
    setEditOpen(true);
  };

  const salvarEdicao = async () => {
    // Sanitização robusta (evita NaN em campos numéricos)
    const safeNum = (v) => {
      if (v === "" || v === null || v === undefined) return undefined;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const safeInt = (v) => {
      if (v === "" || v === null || v === undefined) return undefined;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : undefined;
    };
    try {
      const payload = {
        nome: editForm.nome?.trim() || undefined,
        cargo: editForm.cargo?.trim() || undefined,
        salario_base: safeNum(editForm.salario_base),
        telefone: editForm.telefone?.trim() || undefined,
        email: editForm.email?.trim() || undefined,
        endereco: editForm.endereco?.trim() || undefined,
        carga_horaria_mensal: safeNum(editForm.carga_horaria_mensal),
        dia_pagamento: safeInt(editForm.dia_pagamento),
      };
      // Remove chaves undefined para não sobrescrever valores válidos
      Object.keys(payload).forEach((k) => { if (payload[k] === undefined) delete payload[k]; });
      if (Object.keys(payload).length === 0) {
        toast.error("Nada para atualizar");
        return;
      }
      await api.patch(`/dp/funcionarios/${editFunc.id}`, payload);
      toast.success("Funcionário atualizado");
      setEditOpen(false);
      setEditFunc(null);
      load();
    } catch (e) {
      toast.error(extractError(e) || "Falha ao atualizar");
    }
  };

  const gerarFolha = async () => {
    try {
      await api.post("/dp/folha", {
        funcionario_id: selFunc.id,
        competencia,
        horas_extras: parseFloat(folhaForm.horas_extras),
        percentual_hora_extra: parseFloat(folhaForm.percentual_hora_extra),
        descontos: parseFloat(folhaForm.descontos),
        beneficios: parseFloat(folhaForm.beneficios),
      });
      toast.success("Folha calculada");
      setFolhaOpen(false);
      setFolhaForm({ horas_extras: "0", percentual_hora_extra: "50", descontos: "0", beneficios: "0" });
      load();
    } catch (e) {
      toast.error(extractError(e) || "Erro no cálculo");
    }
  };

  const openFolhaFor = (func) => {
    setSelFunc(func);
    const existing = folhas.find((f) => f.funcionario_id === func.id);
    if (existing) {
      setFolhaForm({
        horas_extras: String(existing.horas_extras),
        percentual_hora_extra: "50",
        descontos: String(existing.descontos),
        beneficios: String(existing.beneficios),
      });
    } else {
      setFolhaForm({ horas_extras: "0", percentual_hora_extra: "50", descontos: "0", beneficios: "0" });
    }
    setFolhaOpen(true);
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-tag mb-2">Departamento Pessoal · CLT + Hora Extra</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl break-anywhere">DP & Folha</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} className="w-44 rounded-sm" data-testid="dp-competencia" />
          <Button
            data-testid="dp-imprimir-pdf"
            variant="outline"
            className="rounded-sm"
            onClick={async () => {
              try {
                const resp = await api.get(`/dp/folha/pdf/${competencia}`, { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) {
                toast.error(extractError(e) || "Nenhuma folha para imprimir");
              }
            }}
          >
            <Printer size={14} className="mr-2" /> Imprimir folha
          </Button>
          {isMaster && (
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
              <DialogTrigger asChild>
                <Button data-testid="btn-novo-funcionario" className="rounded-sm bg-foreground text-background hover:bg-foreground/90">
                  <Plus size={14} className="mr-2" /> Funcionário
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-2xl">
                <DialogHeader><DialogTitle>Novo colaborador</DialogTitle></DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
                  <div className="col-span-1 sm:col-span-2"><Label className="label-tag">Nome completo</Label><Input data-testid="dp-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
                  <div><Label className="label-tag">CPF</Label><Input data-testid="dp-cpf" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} /></div>
                  <div><Label className="label-tag">Cargo</Label><Input data-testid="dp-cargo" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} /></div>
                  <div><Label className="label-tag">Salário base (R$)</Label><Input data-testid="dp-salario" type="number" step="0.01" value={form.salario_base} onChange={(e) => setForm({ ...form, salario_base: e.target.value })} /></div>
                  <div><Label className="label-tag">Carga horária mensal</Label><Input data-testid="dp-carga" type="number" value={form.carga_horaria_mensal} onChange={(e) => setForm({ ...form, carga_horaria_mensal: e.target.value })} /></div>
                  <div><Label className="label-tag">Dia de pagamento</Label><Input data-testid="dp-dia-pgto" type="number" min="1" max="31" value={form.dia_pagamento} onChange={(e) => setForm({ ...form, dia_pagamento: e.target.value })} placeholder="5" /></div>
                  <div><Label className="label-tag">Data admissão</Label><Input data-testid="dp-adm" type="date" value={form.data_admissao} onChange={(e) => setForm({ ...form, data_admissao: e.target.value })} /></div>
                  <div><Label className="label-tag">Telefone</Label><Input data-testid="dp-tel" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
                  <div className="col-span-1 sm:col-span-2"><Label className="label-tag">E-mail</Label><Input data-testid="dp-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="col-span-1 sm:col-span-2"><Label className="label-tag">Endereço</Label><Textarea data-testid="dp-end" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                  <Button data-testid="dp-salvar" onClick={criar} className="bg-foreground text-background hover:bg-foreground/90">Cadastrar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Resumo folha */}
      {resumo && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">Bruto do mês</div>
            <div className="stat-num text-2xl mt-2">{fmtBRL(resumo.total_bruto)}</div>
          </div>
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">Hora extra</div>
            <div className="stat-num text-2xl mt-2 text-warning">{fmtBRL(resumo.total_hora_extra)}</div>
          </div>
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">Encargos (INSS)</div>
            <div className="stat-num text-2xl mt-2 text-destructive">{fmtBRL(resumo.total_encargos)}</div>
          </div>
          <div className="stat-block p-5 rounded-sm">
            <div className="label-tag">Líquido a pagar</div>
            <div className="stat-num text-2xl mt-2 text-success">{fmtBRL(resumo.total_liquido)}</div>
          </div>
        </div>
      )}

      <Tabs defaultValue="funcs">
        <TabsList className="rounded-sm">
          <TabsTrigger value="funcs" data-testid="dp-tab-func"><Users size={12} className="mr-2" />Colaboradores ({funcs.filter((f) => f.ativo).length})</TabsTrigger>
          <TabsTrigger value="folha" data-testid="dp-tab-folha"><DollarSign size={12} className="mr-2" />Folha do mês ({folhas.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="funcs" className="mt-6">
          <Card className="p-6 rounded-sm border-border">
            {funcs.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm border border-dashed rounded-sm">
                Nenhum funcionário cadastrado.
              </div>
            ) : (
              <div className="grid gap-2">
                {funcs.filter((f) => f.ativo).map((f) => (
                  <div key={f.id} data-testid={`func-${f.id}`} className="flex items-center justify-between border border-border p-4 rounded-sm gap-3 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold break-anywhere">{f.nome}</div>
                      <div className="text-xs text-muted-foreground font-mono">CPF {f.cpf} · {f.cargo}</div>
                      <div className="text-xs text-muted-foreground">
                        Salário base <b>{fmtBRL(f.salario_base)}</b> · {f.carga_horaria_mensal}h/mês · Pgto dia <b>{f.dia_pagamento ?? 5}</b>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openFolhaFor(f)} data-testid={`folha-${f.id}`}>
                        <Calculator size={12} className="mr-1" /> Folha
                      </Button>
                      {isMaster && (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(f)} data-testid={`edit-func-${f.id}`} title="Editar funcionário">
                            <Pencil size={14} />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => del(f.id)} data-testid={`del-func-${f.id}`} title="Desligar funcionário">
                            <Trash2 size={14} className="text-destructive" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Inativos / Desligados — permite limpeza definitiva de cadastros fantasmas */}
            {isMaster && funcs.some((f) => !f.ativo) && (
              <div className="mt-6 pt-6 border-t border-dashed border-border">
                <div className="label-tag mb-3 flex items-center gap-2">
                  <Trash2 size={12} className="text-muted-foreground" />
                  Inativos / Desligados ({funcs.filter((f) => !f.ativo).length})
                  <span className="text-muted-foreground text-[10px] normal-case tracking-normal font-normal">
                    — não aparecem em novas folhas, mas ainda ocupam registro
                  </span>
                </div>
                <div className="grid gap-2">
                  {funcs.filter((f) => !f.ativo).map((f) => (
                    <div key={f.id} data-testid={`func-inativo-${f.id}`} className="flex items-center justify-between border border-dashed border-muted-foreground/30 p-3 rounded-sm gap-3 min-w-0 bg-muted/20">
                      <div className="flex-1 min-w-0 opacity-70">
                        <div className="font-semibold break-anywhere">{f.nome}</div>
                        <div className="text-xs text-muted-foreground font-mono">CPF {f.cpf} · {f.cargo} · Inativo</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => delPermanente(f)}
                        data-testid={`del-permanente-${f.id}`}
                        className="border-destructive/40 text-destructive hover:bg-destructive/5"
                        title="Excluir permanentemente do banco de dados"
                      >
                        <Trash2 size={12} className="mr-1" /> Excluir permanentemente
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="folha" className="mt-6">
          <Card className="p-6 rounded-sm border-border">
            {folhas.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm border border-dashed rounded-sm">
                Nenhuma folha calculada para {competencia}. Clique em <b>Folha</b> em cada colaborador.
              </div>
            ) : (
              <div className="overflow-x-auto -mx-6 sm:mx-0 px-6 sm:px-0">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-left label-tag border-b border-border">
                      <th className="py-2">Colaborador</th>
                      <th className="py-2">Base</th>
                      <th className="py-2">H.E.</th>
                      <th className="py-2">Descontos</th>
                      <th className="py-2">INSS</th>
                      <th className="py-2 text-right">Líquido</th>
                      {isMaster && <th className="py-2 text-right">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {folhas.map((f) => (
                      <tr key={f.id} data-testid={`folha-row-${f.id}`} className="border-b border-border/40">
                        <td className="py-3">
                          <div className="font-medium break-anywhere">{f.funcionario_nome}</div>
                          <div className="text-xs text-muted-foreground">{f.cargo}</div>
                        </td>
                        <td className="py-3 font-mono">{fmtBRL(f.salario_base)}</td>
                        <td className="py-3 font-mono text-warning">{fmtBRL(f.total_hora_extra)} <span className="text-xs">({f.horas_extras}h)</span></td>
                        <td className="py-3 font-mono">{fmtBRL(f.descontos)}</td>
                        <td className="py-3 font-mono text-destructive">{fmtBRL(f.inss)}</td>
                        <td className="py-3 text-right stat-num text-lg text-success">{fmtBRL(f.salario_liquido)}</td>
                        {isMaster && (
                          <td className="py-3 text-right whitespace-nowrap">
                            <Button size="icon" variant="ghost" onClick={() => abrirEdicaoFolha(f)} data-testid={`edit-folha-${f.id}`} title="Editar folha">
                              <Pencil size={14} />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => excluirFolha(f.id, f.funcionario_nome)} data-testid={`del-folha-${f.id}`} title="Excluir folha">
                              <Trash2 size={14} className="text-destructive" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={folhaOpen} onOpenChange={setFolhaOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Folha — {selFunc?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Salário base: <b className="text-foreground">{fmtBRL(selFunc?.salario_base || 0)}</b> · Carga: {selFunc?.carga_horaria_mensal}h/mês
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="label-tag flex items-center gap-1"><Clock size={10} /> Horas extras</Label><Input data-testid="folha-he" type="number" step="0.5" value={folhaForm.horas_extras} onChange={(e) => setFolhaForm({ ...folhaForm, horas_extras: e.target.value })} /></div>
              <div><Label className="label-tag">% adicional H.E.</Label><Input data-testid="folha-percent" type="number" value={folhaForm.percentual_hora_extra} onChange={(e) => setFolhaForm({ ...folhaForm, percentual_hora_extra: e.target.value })} /></div>
              <div><Label className="label-tag">Benefícios (R$)</Label><Input data-testid="folha-benef" type="number" step="0.01" value={folhaForm.beneficios} onChange={(e) => setFolhaForm({ ...folhaForm, beneficios: e.target.value })} /></div>
              <div><Label className="label-tag">Descontos (R$)</Label><Input data-testid="folha-desc" type="number" step="0.01" value={folhaForm.descontos} onChange={(e) => setFolhaForm({ ...folhaForm, descontos: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolhaOpen(false)}>Cancelar</Button>
            <Button data-testid="folha-calcular" onClick={gerarFolha} className="bg-foreground text-background hover:bg-foreground/90">Calcular e salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Funcionário */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar funcionário — {editFunc?.nome}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
            <div className="col-span-1 sm:col-span-2">
              <Label className="label-tag">Nome completo</Label>
              <Input data-testid="edit-func-nome" value={editForm.nome} onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })} />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Label className="label-tag">CPF (não editável)</Label>
              <Input data-testid="edit-func-cpf" value={editFunc?.cpf || ""} disabled className="bg-muted/50 text-muted-foreground" />
            </div>
            <div>
              <Label className="label-tag">Cargo</Label>
              <Input data-testid="edit-func-cargo" value={editForm.cargo} onChange={(e) => setEditForm({ ...editForm, cargo: e.target.value })} />
            </div>
            <div>
              <Label className="label-tag">Salário base (R$)</Label>
              <Input data-testid="edit-func-salario" type="number" step="0.01" value={editForm.salario_base} onChange={(e) => setEditForm({ ...editForm, salario_base: e.target.value })} />
            </div>
            <div>
              <Label className="label-tag">Carga horária mensal (h)</Label>
              <Input data-testid="edit-func-carga" type="number" value={editForm.carga_horaria_mensal} onChange={(e) => setEditForm({ ...editForm, carga_horaria_mensal: e.target.value })} />
            </div>
            <div>
              <Label className="label-tag">Dia de pagamento (dia do mês)</Label>
              <Input data-testid="edit-func-dia-pgto" type="number" min="1" max="31" value={editForm.dia_pagamento} onChange={(e) => setEditForm({ ...editForm, dia_pagamento: e.target.value })} placeholder="5" />
            </div>
            <div>
              <Label className="label-tag">Telefone / WhatsApp</Label>
              <Input data-testid="edit-func-tel" value={editForm.telefone} onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })} placeholder="+55 21 99999-8888" />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Label className="label-tag">E-mail</Label>
              <Input data-testid="edit-func-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <Label className="label-tag">Endereço</Label>
              <Textarea data-testid="edit-func-end" value={editForm.endereco} onChange={(e) => setEditForm({ ...editForm, endereco: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button data-testid="edit-func-salvar" onClick={salvarEdicao} className="bg-foreground text-background hover:bg-foreground/90">Salvar alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Editar Folha */}
      <Dialog open={folhaEditOpen} onOpenChange={setFolhaEditOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Editar lançamento de folha</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-tag flex items-center gap-1"><Clock size={10} /> Horas extras</Label>
                <Input data-testid="folha-edit-he" type="number" step="0.5" value={folhaEditForm.horas_extras} onChange={(e) => setFolhaEditForm({ ...folhaEditForm, horas_extras: e.target.value })} />
              </div>
              <div>
                <Label className="label-tag">% adicional H.E.</Label>
                <Input data-testid="folha-edit-percent" type="number" value={folhaEditForm.percentual_hora_extra} onChange={(e) => setFolhaEditForm({ ...folhaEditForm, percentual_hora_extra: e.target.value })} />
              </div>
              <div>
                <Label className="label-tag">Benefícios (R$)</Label>
                <Input data-testid="folha-edit-benef" type="number" step="0.01" value={folhaEditForm.beneficios} onChange={(e) => setFolhaEditForm({ ...folhaEditForm, beneficios: e.target.value })} />
              </div>
              <div>
                <Label className="label-tag">Descontos (R$)</Label>
                <Input data-testid="folha-edit-desc" type="number" step="0.01" value={folhaEditForm.descontos} onChange={(e) => setFolhaEditForm({ ...folhaEditForm, descontos: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="label-tag">Observações</Label>
              <Textarea data-testid="folha-edit-obs" rows={2} value={folhaEditForm.observacoes} onChange={(e) => setFolhaEditForm({ ...folhaEditForm, observacoes: e.target.value })} />
            </div>
            <div className="text-[10px] text-muted-foreground">O INSS e o líquido são recalculados automaticamente ao salvar.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolhaEditOpen(false)}>Cancelar</Button>
            <Button data-testid="folha-edit-salvar" onClick={salvarEdicaoFolha} className="bg-foreground text-background hover:bg-foreground/90">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
